import React, { useState, useEffect } from 'react';
import {
  Table,
  Button,
  Modal,
  Form,
  Input,
  InputNumber,
  Select,
  message,
  Popconfirm,
  Space,
  Card,
  Upload,
  Checkbox,
} from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, UploadOutlined } from '@ant-design/icons';
import api from '../services/api';
import * as XLSX from 'xlsx';

const TarifasBase = () => {
  const [tarifas, setTarifas] = useState([]);
  const [aseguradoras, setAseguradoras] = useState([]);
  const [tiposServicio, setTiposServicio] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [uploadModalVisible, setUploadModalVisible] = useState(false);
  const [editingTarifa, setEditingTarifa] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [form] = Form.useForm();

  useEffect(() => {
    fetchTarifas();
    fetchAseguradoras();
    fetchTiposServicio();
  }, []);

  const fetchTarifas = async () => {
    setLoading(true);
    try {
      const response = await api.get('/tarifas-base');
      setTarifas(response.data);
    } catch (error) {
      message.error('Error al cargar tarifas');
    } finally {
      setLoading(false);
    }
  };

  const fetchAseguradoras = async () => {
    try {
      const response = await api.get('/aseguradoras');
      setAseguradoras(response.data.filter(a => a.estado === 'activa'));
    } catch (error) {
      console.error('Error al cargar aseguradoras');
    }
  };

  const fetchTiposServicio = async () => {
    try {
      const response = await api.get('/tipos-servicio');
      setTiposServicio(response.data.filter(t => t.estado === 'activo'));
    } catch (error) {
      console.error('Error al cargar tipos de servicio');
    }
  };

  const handleSubmit = async (values) => {
    try {
      // Si el checkbox "Incluye IGV" está desmarcado, sumar el 18% al precio
      let precioFinal = parseFloat(values.precio);
      if (!values.incluye_igv) {
        precioFinal = precioFinal * 1.18;
      }

      const dataToSend = {
        ...values,
        precio: parseFloat(precioFinal.toFixed(2))
      };
      // No enviar incluye_igv al backend, solo se usa para el cálculo
      delete dataToSend.incluye_igv;

      if (editingTarifa) {
        await api.put(`/tarifas-base/${editingTarifa.id}`, dataToSend);
        message.success('Tarifa actualizada exitosamente');
      } else {
        await api.post('/tarifas-base', dataToSend);
        message.success('Tarifa creada exitosamente');
      }
      setModalVisible(false);
      form.resetFields();
      setEditingTarifa(null);
      fetchTarifas();
    } catch (error) {
      message.error('Error al guardar tarifa');
    }
  };

  const handleEdit = (record) => {
    setEditingTarifa(record);
    form.setFieldsValue({
      ...record,
      aseguradora_id: record.aseguradora_id?.toString(),
      tipo_servicio_id: record.tipo_servicio_id?.toString(),
      incluye_igv: true, // Por defecto, asumimos que el precio guardado incluye IGV
    });
    setModalVisible(true);
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/tarifas-base/${id}`);
      message.success('Tarifa eliminada exitosamente');
      fetchTarifas();
    } catch (error) {
      message.error('Error al eliminar tarifa');
    }
  };

  const handleFileUpload = async (file) => {
    setUploading(true);
    try {
      const reader = new FileReader();
      reader.onload = async (e) => {
        try {
          const data = new Uint8Array(e.target.result);
          const workbook = XLSX.read(data, { type: 'array' });
          const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
          const jsonData = XLSX.utils.sheet_to_json(firstSheet, { header: 1 });

          if (jsonData.length < 2) {
            message.error('El archivo Excel debe tener al menos una fila de datos (después del encabezado)');
            setUploading(false);
            return;
          }

          // Obtener encabezados (primera fila)
          const headers = jsonData[0].map(h => String(h).toUpperCase().trim());
          
          // Buscar índices de columnas
          const aseguradoraIndex = headers.findIndex(h => h.includes('ASEGURADORA'));
          const tipoServicioIndex = headers.findIndex(h => h.includes('TIPO_SERVICIO') || h.includes('TIPO SERVICIO'));
          const precioIndex = headers.findIndex(h => h.includes('PRECIO'));
          const descripcionIndex = headers.findIndex(h => h.includes('DESCRIPCION') || h.includes('DESCRIPCIÓN'));
          const igvIndex = headers.findIndex(h => h.includes('IGV'));

          if (aseguradoraIndex === -1 || tipoServicioIndex === -1 || precioIndex === -1) {
            message.error('El archivo Excel debe tener las columnas ASEGURADORA, TIPO_SERVICIO y PRECIO');
            setUploading(false);
            return;
          }

          // Procesar datos (desde la segunda fila)
          const tarifasData = [];
          const errores = [];

          for (let i = 1; i < jsonData.length; i++) {
            const row = jsonData[i];
            if (!row || row.length === 0) continue;

            const aseguradoraId = row[aseguradoraIndex] ? parseInt(row[aseguradoraIndex]) : null;
            const tipoServicioCodigo = row[tipoServicioIndex] ? String(row[tipoServicioIndex]).trim() : '';
            const precio = row[precioIndex] ? parseFloat(row[precioIndex]) : null;
            const descripcion = descripcionIndex !== -1 && row[descripcionIndex] ? String(row[descripcionIndex]).trim() : '';
            // Leer columna IGV (SI/NO), por defecto SI si no existe
            const igvValue = igvIndex !== -1 && row[igvIndex] 
              ? String(row[igvIndex]).toUpperCase().trim() 
              : 'SI';
            const incluyeIgv = igvValue === 'SI' || igvValue === 'S' || igvValue === 'YES' || igvValue === 'Y';

            if (!aseguradoraId || !tipoServicioCodigo || precio === null || isNaN(precio)) {
              errores.push(`Fila ${i + 1}: Faltan datos requeridos (ASEGURADORA, TIPO_SERVICIO o PRECIO)`);
              continue;
            }

            tarifasData.push({
              aseguradora_id: aseguradoraId,
              tipo_servicio_codigo: tipoServicioCodigo,
              precio: precio,
              descripcion: descripcion || null,
              incluye_igv: incluyeIgv
            });
          }

          if (tarifasData.length === 0) {
            message.error('No se encontraron datos válidos en el archivo');
            setUploading(false);
            return;
          }

          // Enviar al backend
          const response = await api.post('/tarifas-base/carga-masiva', { tarifas: tarifasData });
          
          if (response.data.exitosos > 0) {
            message.success(`Se cargaron exitosamente ${response.data.exitosos} tarifa(s)`);
          }
          if (response.data.errores && response.data.errores.length > 0) {
            message.warning(`Algunos registros tuvieron errores: ${response.data.errores.length}`);
            console.error('Errores:', response.data.errores);
          }
          if (errores.length > 0) {
            message.warning(`Errores de validación: ${errores.length}`);
            console.error('Errores de validación:', errores);
          }

          setUploadModalVisible(false);
          fetchTarifas();
        } catch (error) {
          console.error('Error al procesar archivo:', error);
          message.error('Error al procesar el archivo Excel: ' + (error.message || 'Error desconocido'));
        } finally {
          setUploading(false);
        }
      };
      reader.readAsArrayBuffer(file);
    } catch (error) {
      console.error('Error al leer archivo:', error);
      message.error('Error al leer el archivo');
      setUploading(false);
    }
    return false; // Prevenir upload automático
  };

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 80,
    },
    {
      title: 'Aseguradora',
      dataIndex: 'aseguradora_nombre',
      key: 'aseguradora',
    },
    {
      title: 'Tipo de Servicio',
      key: 'tipo_servicio',
      render: (_, record) => `${record.tipo_servicio_codigo} - ${record.tipo_servicio_nombre}`,
    },
    {
      title: 'Precio',
      dataIndex: 'precio',
      key: 'precio',
      render: (precio) => `S/ ${precio}`,
    },
    {
      title: 'Descripción',
      dataIndex: 'descripcion',
      key: 'descripcion',
    },
    {
      title: 'Acciones',
      key: 'acciones',
      render: (_, record) => (
        <Space>
          <Button
            type="primary"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          >
            Editar
          </Button>
          <Popconfirm
            title="¿Estás seguro de eliminar esta tarifa?"
            onConfirm={() => handleDelete(record.id)}
            okText="Sí"
            cancelText="No"
          >
            <Button type="primary" danger icon={<DeleteOutlined />}>
              Eliminar
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <Card
        title="Gestión de Tarifas Base"
        extra={
          <Space>
            <Button
              type="default"
              icon={<UploadOutlined />}
              onClick={() => setUploadModalVisible(true)}
            >
              Cargar desde Excel
            </Button>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => {
                setEditingTarifa(null);
                form.resetFields();
                setModalVisible(true);
              }}
            >
              Nueva Tarifa
            </Button>
          </Space>
        }
      >
        <Table
          columns={columns}
          dataSource={tarifas}
          rowKey="id"
          loading={loading}
        />
      </Card>

      <Modal
        title={editingTarifa ? 'Editar Tarifa' : 'Nueva Tarifa'}
        open={modalVisible}
        onCancel={() => {
          setModalVisible(false);
          form.resetFields();
          setEditingTarifa(null);
        }}
        footer={null}
        width={600}
      >
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <Form.Item
            name="aseguradora_id"
            label="Aseguradora"
            rules={[{ required: true, message: 'Selecciona una aseguradora' }]}
          >
            <Select placeholder="Selecciona una aseguradora">
              {aseguradoras.map((aseguradora) => (
                <Select.Option key={aseguradora.id} value={aseguradora.id}>
                  {aseguradora.nombre}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item
            name="tipo_servicio_id"
            label="Tipo de Servicio"
            rules={[{ required: true, message: 'Selecciona un tipo de servicio' }]}
          >
            <Select placeholder="Selecciona un tipo de servicio">
              {tiposServicio.map((tipo) => (
                <Select.Option key={tipo.id} value={tipo.id}>
                  {tipo.codigo} - {tipo.nombre}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item
            name="precio"
            label="Precio"
            rules={[{ required: true, message: 'Ingresa el precio' }]}
          >
            <InputNumber min={0} precision={2} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item
            name="incluye_igv"
            valuePropName="checked"
            initialValue={true}
          >
            <Checkbox>Incluye IGV</Checkbox>
          </Form.Item>
          <Form.Item shouldUpdate={(prevValues, currentValues) => 
            prevValues.incluye_igv !== currentValues.incluye_igv || 
            prevValues.precio !== currentValues.precio
          }>
            {({ getFieldValue }) => {
              const incluyeIgv = getFieldValue('incluye_igv');
              const precio = getFieldValue('precio');
              
              if (!incluyeIgv && precio && precio > 0) {
                const igv = precio * 0.18;
                const total = precio * 1.18;
                
                return (
                  <>
                    <Form.Item label="IGV (18%)">
                      <InputNumber 
                        value={igv.toFixed(2)} 
                        precision={2}
                        readOnly
                        style={{ width: '100%' }}
                        prefix="S/ "
                      />
                    </Form.Item>
                    <Form.Item label="Total (Precio + IGV)">
                      <InputNumber 
                        value={total.toFixed(2)} 
                        precision={2}
                        readOnly
                        style={{ width: '100%' }}
                        prefix="S/ "
                      />
                    </Form.Item>
                  </>
                );
              }
              return null;
            }}
          </Form.Item>
          <Form.Item name="descripcion" label="Descripción">
            <Input.TextArea rows={3} />
          </Form.Item>
          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit">
                {editingTarifa ? 'Actualizar' : 'Crear'}
              </Button>
              <Button
                onClick={() => {
                  setModalVisible(false);
                  form.resetFields();
                  setEditingTarifa(null);
                }}
              >
                Cancelar
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="Cargar Tarifas Base desde Excel"
        open={uploadModalVisible}
        onCancel={() => setUploadModalVisible(false)}
        footer={null}
        width={600}
      >
        <div style={{ marginBottom: 16 }}>
          <p><strong>Formato del archivo Excel:</strong></p>
          <p>El archivo debe tener las siguientes columnas (en la primera fila):</p>
          <ul>
            <li><strong>ASEGURADORA</strong> (ID de la aseguradora, requerido)</li>
            <li><strong>TIPO_SERVICIO</strong> (Código del tipo de servicio, requerido)</li>
            <li><strong>PRECIO</strong> (requerido)</li>
            <li><strong>IGV</strong> (SI/NO, opcional - por defecto SI. Si es NO, se calculará y sumará el 18%)</li>
            <li><strong>DESCRIPCION</strong> (opcional)</li>
          </ul>
          <p style={{ color: '#999', fontSize: '12px' }}>
            Ejemplo:<br />
            ASEGURADORA | TIPO_SERVICIO | PRECIO | IGV | DESCRIPCION<br />
            2 | TS-005 | 70 | SI |<br />
            2 | TS-006 | 50 | NO | (Se guardará como 59.00 con IGV incluido)
          </p>
        </div>
        <Upload
          accept=".xlsx,.xls"
          beforeUpload={handleFileUpload}
          showUploadList={false}
          disabled={uploading}
        >
          <Button type="primary" icon={<UploadOutlined />} loading={uploading} block>
            {uploading ? 'Procesando...' : 'Seleccionar archivo Excel'}
          </Button>
        </Upload>
      </Modal>
    </div>
  );
};

export default TarifasBase;

